import type { TableSortEvent } from '@app/shared/vue/components/sf-table/types'
import type {
  GroupListRow,
  GroupSortDirection,
  SortableGroupColumn,
} from '@app/application/user-management/types'
import type { QueryParam } from '@app/application/user-management/utils/list-query'

import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import {
  groupColumns,
  groupSortDirections,
} from '@app/application/user-management/types'
import {
  FIRST_PAGE,
  GROUPS_REQUEST_KEY,
  QUERY_KEYS,
  STALE_GROUPS_REQUEST_REASON,
} from '@app/application/user-management/constants'
import {
  startLatestRequest,
  toPage,
  toSearch,
  toSortDirection,
} from '@app/application/user-management/utils/list-query'
import { isSortableGroupColumn } from '@app/application/user-management/predicates/is-sortable-group-column'
import { fetchGroups } from '@app/application/user-management/api/groups'
import { SfLog } from '@app/adapters/logger'
import useUrlStorage from '@app/shared/vue/composables/use-url-storage'

const toSortBy = (param: QueryParam): SortableGroupColumn =>
  typeof param === 'string' && isSortableGroupColumn(param)
    ? param
    : groupColumns.NAME

const toParam = (value: string | undefined): string => value ?? ''

export const useGroupsListStore = defineStore('groupsList', () => {
  const sortBy = useUrlStorage<SortableGroupColumn>(
    QUERY_KEYS.SORT_BY,
    groupColumns.NAME,
    String,
    { deserialize: toSortBy }
  )
  const sortDirection = useUrlStorage<GroupSortDirection>(
    QUERY_KEYS.SORT_DIRECTION,
    groupSortDirections.ASC,
    String,
    { deserialize: toSortDirection }
  )
  const page = useUrlStorage<number>(QUERY_KEYS.PAGE, FIRST_PAGE, String, {
    deserialize: toPage,
  })
  const search = useUrlStorage<string | undefined>(
    QUERY_KEYS.SEARCH,
    undefined,
    toParam,
    { deserialize: toSearch }
  )

  const groups = ref<GroupListRow[]>([])
  const totalPages = ref(FIRST_PAGE)
  const loading = ref(true)
  const error = ref(false)

  const setSearch = (term: string): void => {
    search.value = term.trim() || undefined
    page.value = FIRST_PAGE
  }

  const setSort = (sort: TableSortEvent): void => {
    if (!isSortableGroupColumn(sort.sortBy)) {
      return
    }

    sortBy.value = sort.sortBy
    sortDirection.value = toSortDirection(sort.sortDirection)
    page.value = FIRST_PAGE
  }

  const setPage = (next: number): void => {
    page.value = next
  }

  const loadGroups = async (): Promise<void> => {
    const isStale = startLatestRequest(
      GROUPS_REQUEST_KEY,
      STALE_GROUPS_REQUEST_REASON
    )

    loading.value = true
    error.value = false

    try {
      const response = await fetchGroups({
        q: search.value,
        sortBy: sortBy.value,
        sortDirection: sortDirection.value,
        page: page.value,
      })

      if (isStale()) {
        return
      }

      groups.value = response?.groups ?? []
      totalPages.value = response?.pagination?.totalPages ?? FIRST_PAGE

      // A bookmarked page past the end answers with no rows, so the list moves
      // to the last page it does have.
      if (page.value > totalPages.value) {
        setPage(totalPages.value)
      }
    } catch (e) {
      if (isStale()) {
        return
      }

      SfLog.warn('Failed to load the groups list', { extra: { error: e } })

      error.value = true
    } finally {
      if (!isStale()) {
        loading.value = false
      }
    }
  }

  const initialize = (): void => {
    void loadGroups()
  }

  watch([sortBy, sortDirection, page, search], loadGroups)

  return {
    error: computed(() => error.value),
    groups: computed(() => groups.value),
    initialize,
    loading: computed(() => loading.value),
    page: computed(() => page.value),
    search: computed(() => search.value),
    setPage,
    setSearch,
    setSort,
    sortBy: computed(() => sortBy.value),
    sortDirection: computed(() => sortDirection.value),
    totalPages: computed(() => totalPages.value),
  }
})
